import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import CustomModal from '../../../util_components/custom_modal';
import RequiredReasonTextarea from '../../../util_components/required_reason_textarea';
import { CareRequestFields } from '../../../util_components/care_request';
import {
  KIND_LABELS,
  KIND_TEACHER_CHANGE,
  build_create_payload,
  care_api_error,
  format_ist,
  has_errors,
  ist_input_to_iso,
  ist_input_value,
  new_care_draft,
  validate_care_draft
} from '../../../util_components/care_request/care_request_rules';
import {
  load_session_care_requests,
  save_care_request,
  update_care_request,
  upload_care_screenshot
} from '../../../util_components/care_request/care_request_api';

const INITIATOR_LABEL = { TEACHER: 'Teacher', STUDENT: 'Student' };

// "From -> to" of a change row, for the table.
function change_detail(c) {
  if (c.from_time || c.to_time) return `${format_ist(c.from_time)} → ${format_ist(c.to_time)}`;
  if (c.from_teacher || c.to_teacher) return `${c.from_teacher || '--'} → ${c.to_teacher || '--'}`;
  return '';
}

// The draft an "Add details" / "Edit" dialog opens with.
function dialog_draft(change, record) {
  const draft = new_care_draft(change.change_kind, moment(change.change_made_at).valueOf());
  if (!record) return draft;
  return {
    ...draft,
    initiated_by: record.initiated_by,
    requested_at: ist_input_value(moment(record.requested_at).valueOf()),
    note: record.note
  };
}

// PROD-7828 E1: every reschedule / teacher change / cancellation of this session with who asked and when, as Care
// recorded it; "Add details" for a change saved without them, "Edit" (with a note) for a recorded one.
function ChangeRequestsPanel({ session_uuid, reload_token }) {
  const [view, setView] = useState(null);
  const [load_error, setLoadError] = useState('');
  const [dialog, setDialog] = useState(null); // {change, record, draft, errors, edit_note, edit_note_error, busy, error}

  const load = useCallback(() => {
    setLoadError('');
    load_session_care_requests(session_uuid)
      .then((data) => setView(data || { records: [], changes: [], can_edit: false }))
      .catch((e) => setLoadError(care_api_error(e, 'Could not load the change requests.').message));
  }, [session_uuid]);

  useEffect(() => {
    if (session_uuid) load();
  }, [session_uuid, reload_token, load]);

  if (load_error) {
    return (
      <div className="care-requests-panel">
        <h3>Change requests</h3>
        <p style={{ color: 'var(--state-error)' }}>* {load_error}</p>
      </div>
    );
  }
  if (!view || !Array.isArray(view.changes) || view.changes.length === 0) return null;

  const records = {};
  (view.records || []).forEach((r) => {
    records[r.uuid] = r;
  });

  const open = (change) => {
    const record = change.record_uuid ? records[change.record_uuid] : null;
    setDialog({
      change,
      record,
      draft: dialog_draft(change, record),
      errors: {},
      edit_note: '',
      busy: false,
      error: ''
    });
  };

  const save = async () => {
    const { change, record, draft, edit_note } = dialog;
    const errors = validate_care_draft(draft, { now_ms: Date.now(), require_note: true });
    const edit_note_error = !!record && edit_note.trim().length < 3;
    if (has_errors(errors) || edit_note_error) {
      setDialog({ ...dialog, errors, edit_note_error });
      return;
    }
    setDialog({ ...dialog, errors: {}, edit_note_error: false, busy: true, error: '' });
    try {
      const screenshot_key = draft.file
        ? await upload_care_screenshot(session_uuid, draft.file)
        : '';
      if (record) {
        await update_care_request(record.uuid, {
          initiated_by: draft.initiated_by,
          requested_at: ist_input_to_iso(draft.requested_at),
          note: draft.note.trim(),
          ...(screenshot_key ? { screenshot_key } : {}),
          edit_note: edit_note.trim()
        });
      } else {
        await save_care_request(
          build_create_payload(draft, {
            session_uuid,
            screenshot_key,
            change_ref: change.change_ref || undefined
          })
        );
      }
      setDialog(null);
      load();
    } catch (e) {
      setDialog({
        ...dialog,
        busy: false,
        error: care_api_error(e, 'Could not save. Try again.').message
      });
    }
  };

  return (
    <div className="care-requests-panel">
      <h3>Change requests</h3>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Change</th>
              <th>Change made at</th>
              <th>Request initiated by</th>
              <th>Requested at</th>
              <th>Note</th>
              <th>Entered by</th>
              <th>Screenshot</th>
              {view.can_edit ? <th aria-label="Actions" /> : null}
            </tr>
          </thead>
          <tbody>
            {view.changes.map((c) => {
              const r = c.record_uuid ? records[c.record_uuid] : null;
              return (
                <tr key={`${c.change_kind}-${c.change_ref}`}>
                  <td>
                    {KIND_LABELS[c.change_kind] || c.change_kind}
                    {change_detail(c) ? <div>{change_detail(c)}</div> : null}
                  </td>
                  <td>{format_ist(c.change_made_at)}</td>
                  <td>
                    {r ? INITIATOR_LABEL[r.initiated_by] || r.initiated_by : 'Details missing'}
                  </td>
                  <td>{r ? format_ist(r.requested_at) : '--'}</td>
                  <td>{r ? r.note : '--'}</td>
                  <td>
                    {r ? (
                      <>
                        {r.created_by_name} · {format_ist(r.created_date)}
                        {r.modified_by ? (
                          <div>
                            Edited by {r.modified_by_name} · {format_ist(r.modified_date)}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      '--'
                    )}
                  </td>
                  <td>
                    {r && r.screenshot_url ? (
                      <a href={r.screenshot_url} target="_blank" rel="noopener noreferrer">
                        View
                      </a>
                    ) : null}
                    {r && r.screenshot_link_error ? 'Link unavailable' : null}
                  </td>
                  {view.can_edit ? (
                    <td>
                      <button type="button" onClick={() => open(c)}>
                        {r ? 'Edit' : 'Add details'}
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <CustomModal show={!!dialog} close={() => (dialog && dialog.busy ? null : setDialog(null))}>
        {dialog ? (
          <div role="dialog" aria-label="Change request details">
            <h5>
              {KIND_LABELS[dialog.change.change_kind]}:{' '}
              {dialog.record ? 'edit request details' : 'add request details'}
            </h5>
            <CareRequestFields
              draft={dialog.draft}
              errors={dialog.errors}
              onChange={(patch) => setDialog({ ...dialog, draft: { ...dialog.draft, ...patch } })}
              initiatedByFixed={dialog.change.change_kind === KIND_TEACHER_CHANGE ? 'TEACHER' : ''}
              requestedLabel={
                dialog.change.change_kind === KIND_TEACHER_CHANGE
                  ? 'Teacher request time (IST)'
                  : 'Requested at (IST)'
              }
              changeMadeAtText={format_ist(dialog.change.change_made_at)}
              showNote
            />
            {dialog.record ? (
              <RequiredReasonTextarea
                value={dialog.edit_note}
                onChange={(e) =>
                  setDialog({ ...dialog, edit_note: e.target.value, edit_note_error: false })
                }
                placeholder="Why are you changing this record? (required)"
                error={!!dialog.edit_note_error}
                errorText="A note (at least 3 characters) is required to edit this record"
                height="60px"
              />
            ) : null}
            {dialog.error ? <p style={{ color: 'var(--state-error)' }}>* {dialog.error}</p> : null}
            <button type="button" disabled={dialog.busy} onClick={save}>
              {dialog.busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : null}
      </CustomModal>
    </div>
  );
}

ChangeRequestsPanel.propTypes = {
  session_uuid: PropTypes.string,
  reload_token: PropTypes.number
};

ChangeRequestsPanel.defaultProps = {
  session_uuid: '',
  reload_token: 0
};

export default ChangeRequestsPanel;
